import React, { useMemo } from "react";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ListChecks,
  User as UserIcon,
  Clock,
  LockKey,
} from "@phosphor-icons/react";
import { ProjectTask } from "shared/types/renovation";
import { useUsers } from "shared/contexts/users.context";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";

interface ProjectTaskCardProps {
  task: ProjectTask;
  onClick: () => void;
  onBlockClick?: (task: ProjectTask) => void;
  isHighlighted?: boolean;
}

export const ProjectTaskCard = React.memo<ProjectTaskCardProps>(({
  task,
  onClick,
  onBlockClick,
  isHighlighted = false,
}) => {
  const { users } = useUsers();

  // MEMOIZE: Expensive user lookup (prevents re-computation on every render)
  const assignedUser = useMemo(
    () => users.find((user) => user.id === task.assignedTo),
    [users, task.assignedTo]
  );

  // MEMOIZE: User name calculation
  const userName = useMemo(() => {
    if (!assignedUser) return "Onbekende gebruiker";
    const firstName = assignedUser.firstName || assignedUser.first_name || "";
    const lastName = assignedUser.lastName || assignedUser.last_name || "";
    return `${firstName} ${lastName}`.trim() || "Onbekende gebruiker";
  }, [assignedUser]);

  // MEMOIZE: User image URL
  const userImageUrl = useMemo(
    () => task.assignedTo ? `https://img.clerk.com/${task.assignedTo}` : null,
    [task.assignedTo]
  );

  // MEMOIZE: Expensive date formatting (prevents re-computation on every render)
  const timeAgo = useMemo(
    () => formatDistanceToNow(new Date(task.createdAt), {
      addSuffix: true,
      locale: nl,
    }),
    [task.createdAt]
  );

  // MEMOIZE: TODO calculations (prevents re-filtering on every render)
  const { totalTodos, completedTodos, hasProgress } = useMemo(() => {
    const total = task.todos?.length || 0;
    const completed = task.todos?.filter(
      (todo) => todo.isCompleted || todo.isNotApplicable
    ).length || 0;
    return {
      totalTodos: total,
      completedTodos: completed,
      hasProgress: total > 0,
    };
  }, [task.todos]);

  // MEMOIZE: Project phase label
  const phaseLabel = useMemo(() => {
    if (!task.projectPhase) return "Geen fase";

    switch (task.projectPhase) {
      case "Ontwerp fase":
        return "Ontwerp";
      case "Gepland":
        return "Uitvoeringsdossier";
      case "In uitvoering":
        return "In uitvoering";
      case "Afgerond":
        return "Voorlopige oplevering";
      case "Volledig gefactureerd":
        return "Definitieve oplevering";
      default:
        return "Geen fase";
    }
  }, [task.projectPhase]);

  return (
    <Card
      className={`cursor-pointer transition-[transform,shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.98] ${
        isHighlighted ? "ring-moss-primary shadow-lg ring-2" : ""
      } ${task.isBlocked ? "bg-amber-50/30 opacity-75" : ""}`}
      onClick={onClick}
      style={{ willChange: 'transform' }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="group flex min-w-0 flex-1 items-start gap-2">
            <ListChecks
              weight="duotone"
              size={18}
              className="text-moss-primary mt-0.5 shrink-0 transition-transform group-hover:scale-110"
            />
            <h3 className="group-hover:text-moss-primary line-clamp-2 flex-1 text-sm font-medium transition-colors">
              {task.title}
            </h3>
          </div>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs">
          <div className="flex flex-1 items-center gap-2 flex-wrap">
            <Badge className="bg-moss-primary/10 text-moss-primary border-0 px-2 py-0.5 text-xs transition-all hover:scale-105">
              TASK
            </Badge>
            <span className="hover:text-moss-primary font-mono text-neutral-400 transition-colors">
              T-{task.id}
            </span>
            <Badge
              variant="outline"
              className="border-neutral-300 text-neutral-600 px-2 py-0.5 text-xs"
            >
              {phaseLabel}
            </Badge>
          </div>
          {hasProgress && (
            <div className="flex items-center gap-1.5 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1">
              <ListChecks
                size={16}
                weight="bold"
                className="text-moss-primary shrink-0"
              />
              <span
                className={`text-xs font-semibold ${completedTodos === totalTodos ? "text-moss-primary" : "text-neutral-600"}`}
              >
                {completedTodos}/{totalTodos}
              </span>
            </div>
          )}
          {task.isBlocked && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    className="cursor-pointer border-amber-300 bg-amber-100 px-2 py-0.5 text-xs text-amber-800 transition-all hover:scale-105 hover:bg-amber-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      onBlockClick?.(task);
                    }}
                  >
                    <LockKey size={14} weight="bold" className="mr-1" />
                    {task.blockedType === "internal"
                      ? "Intern geblokkeerd"
                      : "Extern geblokkeerd"}
                  </Badge>
                </TooltipTrigger>
                {task.blockedReason && (
                  <TooltipContent
                    side="top"
                    className="max-w-xs bg-neutral-800 text-white"
                  >
                    <p className="text-xs leading-relaxed">
                      {task.blockedReason}
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {task.description && (
          <p className="line-clamp-3 text-xs text-neutral-600">
            {task.description}
          </p>
        )}

        <div className="flex items-center justify-between gap-2">
          {/* Assignee with avatar */}
          {task.assignedTo && assignedUser ? (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <img
                src={userImageUrl || ''}
                alt={userName}
                className="border-moss-primary/30 h-6 w-6 shrink-0 rounded-full border"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=31403B&color=F9F0EE&size=24`;
                }}
              />
              <span className="truncate text-xs text-neutral-600">
                {userName}
              </span>
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-neutral-400 italic">
              <UserIcon size={14} className="shrink-0" />
              <span className="truncate">Niet toegewezen</span>
            </div>
          )}

          {/* Time ago */}
          <div className="flex shrink-0 items-center gap-1 text-xs text-neutral-400">
            <Clock size={12} className="shrink-0" />
            <span className="whitespace-nowrap">{timeAgo}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

ProjectTaskCard.displayName = "ProjectTaskCard";
